import type {
  AdminAlertDispatchRow,
  AdminAlertPrefRow,
  AdminCreditLedgerRow,
  AdminEmailVerificationRow,
  AdminFeedbackRow,
  AdminJobRunApiRow,
  AdminOrderDetail,
  AdminOrderRow,
  AdminOverview,
  AdminPlanRow,
  AdminPaged,
  AdminSubscriptionRow,
  AdminUserDetailResponse,
  AdminUsersPageResponse,
} from "./types";
import { adminFetchCredentials, apiUrl } from "../lib/apiOrigin";

const TOKEN_KEY = "xtool_token";

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { detail?: unknown };
    const d = data.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d) && d[0] && typeof (d[0] as { msg?: string }).msg === "string")
      return (d[0] as { msg: string }).msg;
  } catch {
    /* ignore */
  }
  return fallback;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

function authHeaders(): HeadersInit {
  const t = getToken();
  if (!t) return {};
  return { Authorization: `Bearer ${t}` };
}

async function adminGet<T>(pathWithQuery: string): Promise<"forbidden" | "error" | T> {
  const res = await fetch(apiUrl(`/api/admin${pathWithQuery}`), {
    headers: { ...authHeaders(), Accept: "application/json" },
    credentials: adminFetchCredentials(),
  });
  if (res.status === 403) return "forbidden";
  if (!res.ok) return "error";
  return (await res.json()) as T;
}

export async function fetchAdminUsersPage(params: {
  page: number;
  page_size: number;
  q?: string;
}): Promise<"forbidden" | "error" | AdminUsersPageResponse> {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.q) sp.set("q", params.q);
  const res = await fetch(apiUrl(`/api/admin/users?${sp}`), {
    headers: { ...authHeaders(), Accept: "application/json" },
    credentials: adminFetchCredentials(),
  });
  if (res.status === 403) return "forbidden";
  if (!res.ok) return "error";
  return (await res.json()) as AdminUsersPageResponse;
}

export async function fetchAdminOrdersPage(params: {
  page: number;
  page_size: number;
  status?: string;
  user_id?: string;
}): Promise<"forbidden" | "error" | AdminPaged<AdminOrderRow>> {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.status) sp.set("status", params.status);
  if (params.user_id?.trim()) sp.set("user_id", params.user_id.trim());
  return adminGet<AdminPaged<AdminOrderRow>>(`/orders?${sp}`);
}

export async function fetchAdminOrderDetail(
  orderId: string,
): Promise<"forbidden" | "error" | AdminOrderDetail> {
  return adminGet<AdminOrderDetail>(`/orders/${encodeURIComponent(orderId)}`);
}

export async function fetchAdminSubscriptionsPage(params: {
  page: number;
  page_size: number;
  status?: string;
  user_id?: string;
}): Promise<"forbidden" | "error" | AdminPaged<AdminSubscriptionRow>> {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.status) sp.set("status", params.status);
  if (params.user_id?.trim()) sp.set("user_id", params.user_id.trim());
  return adminGet<AdminPaged<AdminSubscriptionRow>>(`/subscriptions?${sp}`);
}

export async function fetchAdminCreditLedgerPage(params: {
  page: number;
  page_size: number;
  user_id?: string;
}): Promise<"forbidden" | "error" | AdminPaged<AdminCreditLedgerRow>> {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.user_id?.trim()) sp.set("user_id", params.user_id.trim());
  return adminGet<AdminPaged<AdminCreditLedgerRow>>(`/credit-ledger?${sp}`);
}

export async function fetchAdminFeedbackPage(params: {
  page: number;
  page_size: number;
  status?: string;
}): Promise<"forbidden" | "error" | AdminPaged<AdminFeedbackRow>> {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.status) sp.set("status", params.status);
  return adminGet<AdminPaged<AdminFeedbackRow>>(`/feedback?${sp}`);
}

export async function fetchAdminJobRunsPage(params: {
  page: number;
  page_size: number;
  job_key?: string;
}): Promise<"forbidden" | "error" | AdminPaged<AdminJobRunApiRow>> {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.job_key?.trim()) sp.set("job_key", params.job_key.trim());
  return adminGet<AdminPaged<AdminJobRunApiRow>>(`/regulatory/job-runs?${sp}`);
}

export async function fetchAdminAlertDispatchesPage(params: {
  page: number;
  page_size: number;
  user_id?: string;
}): Promise<"forbidden" | "error" | AdminPaged<AdminAlertDispatchRow>> {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.user_id?.trim()) sp.set("user_id", params.user_id.trim());
  return adminGet<AdminPaged<AdminAlertDispatchRow>>(
    `/regulatory/alert-dispatches?${sp}`,
  );
}

export async function fetchAdminAlertPrefsPage(params: {
  page: number;
  page_size: number;
  enabled_only?: boolean;
}): Promise<"forbidden" | "error" | AdminPaged<AdminAlertPrefRow>> {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.enabled_only) sp.set("enabled_only", "true");
  return adminGet<AdminPaged<AdminAlertPrefRow>>(`/regulatory/alert-prefs?${sp}`);
}

export async function fetchAdminEmailVerificationPage(params: {
  page: number;
  page_size: number;
}): Promise<"forbidden" | "error" | AdminPaged<AdminEmailVerificationRow>> {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  return adminGet<AdminPaged<AdminEmailVerificationRow>>(
    `/email-verification-events?${sp}`,
  );
}

export async function fetchAdminUserDetail(
  userId: string,
): Promise<"forbidden" | "error" | AdminUserDetailResponse> {
  const res = await fetch(apiUrl(`/api/admin/users/${encodeURIComponent(userId)}`), {
    headers: { ...authHeaders(), Accept: "application/json" },
    credentials: adminFetchCredentials(),
  });
  if (res.status === 403) return "forbidden";
  if (!res.ok) return "error";
  return (await res.json()) as AdminUserDetailResponse;
}

export type AdminPlanPatchBody = {
  name?: string;
  price_usd_cents?: number;
  billing_cycle?: string;
  scan_quota?: number;
  is_unlimited?: boolean;
  is_active?: boolean;
  sort_order?: number;
};

export async function fetchAdminPlans(): Promise<
  "forbidden" | "error" | { items: AdminPlanRow[] }
> {
  return adminGet<{ items: AdminPlanRow[] }>("/plans");
}

export async function patchAdminPlan(
  planId: string,
  body: AdminPlanPatchBody,
): Promise<"forbidden" | "error" | AdminPlanRow> {
  const res = await fetch(apiUrl(`/api/admin/plans/${encodeURIComponent(planId)}`), {
    method: "PATCH",
    headers: {
      ...authHeaders(),
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: adminFetchCredentials(),
    body: JSON.stringify(body),
  });
  if (res.status === 403) return "forbidden";
  if (!res.ok) return "error";
  return (await res.json()) as AdminPlanRow;
}

export async function fetchAdminOverview(): Promise<
  "forbidden" | "error" | AdminOverview
> {
  const res = await fetch(apiUrl("/api/admin/overview"), {
    headers: {
      ...authHeaders(),
      Accept: "application/json",
    },
    credentials: adminFetchCredentials(),
  });
  if (res.status === 403) return "forbidden";
  if (!res.ok) return "error";
  return (await res.json()) as AdminOverview;
}

export async function fetchMe(): Promise<{ role?: string; email?: string } | null> {
  const res = await fetch(apiUrl("/api/auth/me"), {
    headers: { ...authHeaders(), Accept: "application/json" },
    credentials: adminFetchCredentials(),
  });
  if (!res.ok) return null;
  return (await res.json()) as { role?: string; email?: string };
}

/** 邮箱密码登录；成功写入 token。若以非白名单账号登录，会清除 token 并返回错误文案。 */
export async function loginWithPassword(
  email: string,
  password: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const res = await fetch(apiUrl("/api/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: adminFetchCredentials(),
    body: JSON.stringify({ email: email.trim(), password }),
  });
  if (res.status === 401) {
    const msg = await readErrorMessage(res, "邮箱或密码错误");
    return { ok: false, message: msg };
  }
  if (!res.ok) {
    const msg = await readErrorMessage(res, "登录失败");
    return { ok: false, message: msg || "无法连接后端，请确认服务已启动" };
  }
  const data = (await res.json()) as { access_token?: string };
  const token = data.access_token?.trim();
  if (!token) return { ok: false, message: "登录响应异常" };

  const adminRes = await fetch(apiUrl("/api/admin/overview"), {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    credentials: adminFetchCredentials(),
  });
  if (adminRes.status === 403) {
    clearToken();
    return {
      ok: false,
      message: "该账号无权进入运维台（未在管理员白名单内，见 backend sql/014 或 ADMIN_CONSOLE_USER_IDS）",
    };
  }
  if (!adminRes.ok) {
    clearToken();
    return { ok: false, message: "已登录但无法访问管理接口，请检查后端配置" };
  }

  setToken(token);
  return { ok: true };
}

/** 探测 API 是否可达（用于登录页状态指示） */
export async function probeApiLatencyMs(): Promise<number | null> {
  const t0 = performance.now();
  try {
    const res = await fetch(apiUrl("/api/plans"), {
      headers: { Accept: "application/json" },
      credentials: adminFetchCredentials(),
    });
    if (!res.ok) return null;
    return Math.round(performance.now() - t0);
  } catch {
    return null;
  }
}
